import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Space, Typography } from "antd";
import { firebase } from "../../../firebase/initFirebase";
import { checkProductBarcode, updateProductBarcode } from "../../../utils/productBarcodes";

export default function ProductBarcodeEditor({ productId, user, onSaved }) {
  const [value, setValue] = useState("");
  const [savedValue, setSavedValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const requestRef = useRef(0);

  useEffect(() => {
    const request = ++requestRef.current;
    setLoading(true);
    setBusy(false);
    setValue("");
    setSavedValue("");
    setStatus(null);
    if (user?.role !== "admin") return;
    firebase.firestore().collection("products").doc(productId).get().then((product) => {
      if (request !== requestRef.current) return;
      if (!product.exists) throw new Error("Product no longer exists");
      const code = product.data().barcode_number || "";
      setValue(code);
      setSavedValue(code);
    }).catch(() => {
      if (request === requestRef.current) setStatus({ error: true, text: "Unable to load current barcode. Close and reopen the product to retry." });
    }).finally(() => { if (request === requestRef.current) setLoading(false); });
    return () => { requestRef.current += 1; };
  }, [productId, user?.role]);

  if (user?.role !== "admin") return null;

  const perform = async (save) => {
    if (busy || loading) return;
    const request = ++requestRef.current;
    setBusy(true);
    setStatus(null);
    try {
      if (save) {
        const code = await updateProductBarcode(productId, value);
        if (request !== requestRef.current) return;
        setValue(code);
        setSavedValue(code);
        setStatus({ text: "Barcode saved. Previous labels will still scan." });
        onSaved?.(code);
      } else {
        const available = await checkProductBarcode(value, productId);
        if (request !== requestRef.current) return;
        setStatus(available ? { text: "Barcode is available for this product." }
          : { error: true, text: "This barcode already exists for another product." });
      }
    } catch (error) {
      if (request === requestRef.current) setStatus({ error: true, text: error.message || "Unable to save barcode." });
    } finally {
      if (request === requestRef.current) setBusy(false);
    }
  };

  return <div style={{ margin: "16px 0" }}>
    <Typography.Text strong>Product barcode (admin only)</Typography.Text>
    <div><Typography.Text type="secondary">10 digits. Previous numbers remain reserved for old labels.</Typography.Text></div>
    <Space wrap style={{ marginTop: 8 }}>
      <Input aria-label="Product barcode" value={value} maxLength={10} inputMode="numeric"
        disabled={loading || busy} placeholder="Enter 10-digit barcode"
        onChange={(event) => { setValue(event.target.value); setStatus(null); }} />
      <Button disabled={loading || busy || !value.trim()} onClick={() => perform(false)}>Check availability</Button>
      <Button type="primary" loading={busy} disabled={loading || busy || !value.trim() || value.trim() === savedValue}
        onClick={() => perform(true)}>Save barcode</Button>
    </Space>
    {status && <div role={status.error ? "alert" : "status"} style={{ marginTop: 8 }}>
      <Typography.Text type={status.error ? "danger" : "success"}>{status.text}</Typography.Text>
    </div>}
  </div>;
}
